import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius } from '@liminal/brand/scale';
import { Button } from '@liminal/web-ui/primitives/Button';
import type { MerchantImageUploads } from '@/hooks/useMerchantImages';
import { RESIZE_TARGETS, type MerchantLogo } from '@/lib/merchantImages';
import { ImageDropZone } from './ImageDropZone';
import { ImageUploadRow } from './ImageUploadRow';

const LOGO_SIZE = 96;

interface MerchantLogoFieldProps {
  /** Images need a merchant to belong to, so a partner not yet saved has none. */
  readonly merchantSaved: boolean;
  readonly logo: MerchantLogo | null;
  /** Clears the logo; Save writes it. */
  readonly onRemove: () => void;
  readonly uploads: MerchantImageUploads;
  readonly disabled: boolean;
}

/**
 * The partner form's single logo slot. A new image replaces the one shown,
 * and like a removal it takes effect when the form is saved.
 */
export function MerchantLogoField({ merchantSaved, logo, onRemove, uploads, disabled }: MerchantLogoFieldProps) {
  if (!merchantSaved) {
    return (
      <Typography variant="body2" color="text.secondary">
        Save the partner first, then add its logo.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {logo && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            component="img"
            src={logo.uri}
            alt="Logo"
            sx={{
              width: LOGO_SIZE,
              height: LOGO_SIZE,
              objectFit: 'contain',
              borderRadius: `${borderRadius.md}px`,
              border: `1px solid ${webColors.border.light}`,
              bgcolor: webColors.surface.primary,
            }}
          />
          <Button variant="link" size="sm" disabled={disabled} onClick={onRemove}>
            Remove
          </Button>
        </Stack>
      )}

      {uploads.uploads.map((upload) => (
        <ImageUploadRow
          key={upload.key}
          upload={upload}
          onRetry={() => uploads.retry(upload.key)}
          onDismiss={() => uploads.dismiss(upload.key)}
        />
      ))}

      <ImageDropZone
        label={logo ? 'Replace the logo' : 'Add a logo'}
        prompt={`Drop a square JPEG, PNG, WebP or SVG here. It is scaled to ${RESIZE_TARGETS.logo.maxEdge} px, and a PNG or SVG keeps its transparency.`}
        buttonLabel={logo ? 'Choose another' : 'Choose a logo'}
        multiple={false}
        disabled={disabled || uploads.busy}
        onFiles={(files) => uploads.add(files, 1)}
      />
    </Stack>
  );
}
